// ============================================================================
// JARVISTRAVEL - THE SECOND FACTOR, IN ONE PLACE (JAR-1537)
//
// Every path that ends in a second factor comes through here: signing in
// (JAR-1391), setting a new password on a temporary one (JAR-1445), and
// resetting a forgotten password (JAR-1481). This is the app's ONLY call to
// signIn.attemptSecondFactor, and it should stay that way.
//
// It used to be a closure inside SignInScreen over that component's state, so
// no other screen could finish an MFA sign-in. ForgotPasswordScreen worked round
// that by sending the user back to sign in with the password they had just set:
// two steps where one would do. Copying the panel instead would have made a
// second attemptSecondFactor call site, which is exactly the second choke point
// the JAR-1391 / JAR-1445 pattern exists to prevent: two copies drift, the way
// describeIncompleteStatus was corrected twice on one screen and never on the
// other.
//
// prepareSecondFactor ONLY FOR email_code. Clerk requires prepare* for factors it
// has to SEND. A TOTP code comes from the authenticator app the user already
// holds, and a backup code was issued at enrolment; there is nothing to prepare,
// and calling it for `totp` errors. A test on SignInScreen pins that.
//
// email_code is the Device Trust factor (JAR-1708): on a new device, an account
// with NO second factor enrolled is asked to confirm it is them, and Clerk
// offers that as an emailed code in supportedSecondFactors. This app used to
// refuse it, which meant a correct password on a new device was a dead end.
//
// phone_code is the enrolled-alternative second factor (JAR-2872): the instance
// offers SMS beside the authenticator app, so an account can hold a phone
// factor where it used to hold none. It completes like email_code — Clerk has
// to SEND it, so enter() prepares it and Resend follows the same cooldown — and
// like TOTP it can sit alongside backup codes. Since JAR-3581 (Brent) the
// texted code leads whenever the account holds a phone, an authenticator too or
// not: enter() sends it, and the panel offers the authenticator below. Clerk
// bills each text, a cost Brent accepted for the simpler sign-in.
//
// email_link is still not completable here: a link is a redirect flow rather
// than a code. Backup codes share the flow because the difference is one
// strategy string, and the alternative is a user with a lost phone and no way
// in.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { useSignIn } from '@clerk/clerk-react';
import { clerkErrorMessage } from '../../utils/clerkError';
import { RESEND_COOLDOWN_SECONDS } from './emailCode';
import { describeIncompleteStatus, describeUnsupportedSecondFactor, SIGN_IN_STILL_LOADING } from './signInStatus';

// Said wherever the code was accepted and sign-in still did not finish: no session
// id came back, or the session would not activate. One sentence, so the two
// cannot drift apart.
const SIGN_IN_NOT_FINISHED = "Your code was accepted, but sign-in didn't finish. Please try again.";

export interface SecondFactorOptions {
  totp: boolean;
  backup: boolean;
}

/** A factor Clerk sends on this app's behalf: an emailed code or a texted one. */
interface SentFactor {
  strategy: 'email_code' | 'phone_code';
  /** The address or phone number the code goes to, as Clerk named it. */
  factorId: string;
  /** Clerk's masked form of it, so it is safe to show on screen. */
  safeIdentifier: string;
}

/** What a factor in supportedSecondFactors carries that this hook reads. */
type OfferedFactor = { strategy: string; emailAddressId?: string; phoneNumberId?: string; safeIdentifier?: string };

/** An emailed-code factor that names the address to send it to. */
function isEmailCodeFactor(f: OfferedFactor): f is OfferedFactor & { emailAddressId: string } {
  return f.strategy === 'email_code' && typeof f.emailAddressId === 'string' && f.emailAddressId !== '';
}

/** A texted-code factor that names the phone number to send it to. */
function isPhoneCodeFactor(f: OfferedFactor): f is OfferedFactor & { phoneNumberId: string } {
  return f.strategy === 'phone_code' && typeof f.phoneNumberId === 'string' && f.phoneNumberId !== '';
}

/** Where this attempt's emailed code stands. */
export type SendState = 'idle' | 'sending' | 'sent' | 'failed';

interface UseSecondFactorArgs {
  /** Called once the second factor completes and the session is active. */
  onComplete: (email: string, remember: boolean) => void;
}

export function useSecondFactor({ onComplete }: UseSecondFactorArgs) {
  const { isLoaded, signIn, setActive } = useSignIn();

  const [active, setIsActive] = useState(false);
  const [code, setCode] = useState('');
  // How this attempt verifies when the account has choices. The verify switch
  // below is the authority: an explicit backup-code toggle wins, then the code
  // Clerk sends (texted for an enrolled phone, emailed for Device Trust), then
  // the authenticator. `usePhoneCode` selects the sent-code BASE mode — an
  // attempt on an account with a phone starts on its texted code (JAR-3581).
  // `useBackupCode` is the fallback overlay on top
  // of either base (phone or TOTP), so the two flags can legitimately both be
  // true; they only ever disagree about which way verify goes, and verify reads
  // backup first.
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [usePhoneCode, setUsePhoneCode] = useState(false);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [options, setOptions] = useState<SecondFactorOptions>({ totp: false, backup: false });
  const [email, setEmail] = useState('');
  const [remember, setRemember] = useState(false);
  // Set only when the attempt is completed by a code Clerk sends. Its presence
  // IS the mode, so there is no second flag that could disagree with it.
  const [sentFactor, setSentFactor] = useState<SentFactor | null>(null);
  // One value rather than flags, so the panel can never say a code was sent and
  // that sending failed at the same time.
  const [sendState, setSendState] = useState<SendState>('idle');
  // Seconds until Resend is offered again, counting down after a code is sent.
  const [resendIn, setResendIn] = useState(0);
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Bumped whenever the attempt on screen is abandoned: reset(), or the screen
  // unmounting. verify() checks it before activating, so a late answer to an
  // abandoned attempt signs nobody in (web-app#384 review). The screens also disable
  // their ways out while a code verifies; this makes that rule the hook's own.
  const attempt = useRef(0);
  useEffect(() => {
    const current = attempt;
    return () => {
      current.current += 1;
    };
  }, []);
  // In flight, as a ref: two submits from ONE render both read isVerifying as
  // false, so that state cannot be the guard (web-app#384 review).
  const inFlight = useRef(false);
  // The attempt whose code is being sent, as a ref for the same reason: two Resend
  // presses in one render both read sendState before either send starts.
  const sendingFor = useRef<number | null>(null);

  /**
   * Enter the second factor for an attempt Clerk says needs one.
   *
   * Returns the refusal to show when this app cannot complete any factor the
   * account has, or null once the panel is up. One function, so no two paths can
   * disagree about which factors are completable: the first version assumed TOTP
   * for every account, and an account whose second factor is a phone or email
   * code was asked for an authenticator code that was never sent — a dishonest
   * dead end (web-app#305 review). Read what Clerk says this attempt supports.
   *
   * `codeSent` is for picking up an attempt a reload interrupted: the emailed code
   * already sent still works, so the panel opens without sending another
   * (JAR-2492). Resend is there if it didn't arrive.
   */
  const enter = (
    factors: ReadonlyArray<OfferedFactor> | null | undefined,
    forEmail: string,
    forRemember: boolean,
    codeSent = false,
  ): string | null => {
    const offered = factors ?? [];
    const strategies = offered.map((f) => String(f.strategy));
    const totp = strategies.includes('totp');
    const backup = strategies.includes('backup_code');
    // A phone factor rides beside the others: it is what an account enrolled
    // instead of an authenticator, so it is completable wherever it appears.
    const offeredPhone = offered.find(isPhoneCodeFactor);
    // An emailed code only when the account has nothing it already holds. Clerk
    // does not raise Device Trust for an account with MFA enrolled, so the two
    // should not arrive together, but if they do the factor in the person's hand
    // wins: no email is sent that nobody asked for.
    const offeredEmail = !totp && !backup && !offeredPhone ? offered.find(isEmailCodeFactor) : undefined;
    if (!totp && !backup && !offeredPhone && !offeredEmail) {
      console.error('[useSecondFactor] second factor this app cannot complete', { strategies });
      return describeUnsupportedSecondFactor(strategies);
    }
    // The attempt STARTS on the texted code whenever the account holds a phone
    // (JAR-3581), and on the authenticator only when there is no phone. Backup
    // codes are the fallback below either.
    const startsOnPhone = !!offeredPhone;
    const nextSent: SentFactor | null = offeredPhone
      ? { strategy: 'phone_code', factorId: offeredPhone.phoneNumberId, safeIdentifier: offeredPhone.safeIdentifier ?? '' }
      : offeredEmail
        ? { strategy: 'email_code', factorId: offeredEmail.emailAddressId, safeIdentifier: offeredEmail.safeIdentifier ?? '' }
        : null;
    setOptions({ totp, backup });
    setSentFactor(nextSent);
    setUseBackupCode(!totp && !offeredPhone && backup);
    setUsePhoneCode(startsOnPhone);
    // A fresh attempt starts clean whether or not the caller reset() first
    // (web-app#384 review). isVerifying is left alone on purpose: if a verify were
    // in flight, stopping its spinner would offer a second submit while the first
    // could still land.
    setCode('');
    setError('');
    setEmail(forEmail);
    setRemember(forRemember);
    setSendState(nextSent && codeSent ? 'sent' : 'idle');
    setResendIn(0);
    setIsActive(true);
    // Send on entry only when this attempt is being completed by the sent code:
    // the Device Trust email, or the texted code of an account with a phone. An
    // attempt that starts on the authenticator sends nothing until asked.
    if (nextSent && !codeSent && (startsOnPhone || nextSent.strategy === 'email_code')) {
      void send(nextSent, attempt.current);
    }
    return null;
  };

  /**
   * Ask Clerk to send the code — by email (Device Trust) or by text (an
   * enrolled phone factor). Separate from verify, and it never touches
   * isVerifying: sending is not checking, and a slow send must not disable the
   * code field for someone whose code has already arrived from an earlier send.
   */
  const send = async (factor: SentFactor, forAttempt: number) => {
    // enter() is only reached after an isLoaded-guarded Clerk call, so this is
    // not expected, but saying so beats a send that silently never happens.
    if (!isLoaded) {
      setError(SIGN_IN_STILL_LOADING);
      return;
    }
    sendingFor.current = forAttempt;
    setSendState('sending');
    try {
      await signIn.prepareSecondFactor(
        factor.strategy === 'phone_code'
          ? { strategy: 'phone_code', phoneNumberId: factor.factorId }
          : { strategy: 'email_code', emailAddressId: factor.factorId },
      );
      // An abandoned attempt's send changes nothing on the attempt now on screen.
      if (attempt.current !== forAttempt) return;
      setSendState('sent');
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      // An abandoned attempt says nothing, the same rule verify follows.
      if (attempt.current !== forAttempt) return;
      setSendState('failed');
      setError(clerkErrorMessage(err, "We couldn't send the code. Use Resend code to try again."));
    } finally {
      // Only this attempt's own send clears the ref. A send abandoned by reset()
      // that settles late must not free Resend while the new attempt's send runs.
      if (sendingFor.current === forAttempt) sendingFor.current = null;
    }
  };

  const resend = () => {
    // Standing-on-sent, the same derivation verify reads: a resend is a new
    // copy of the code this attempt is being completed WITH. A carried phone
    // factor beside a held authenticator has nothing to resend while the
    // person is on the authenticator (web-app#859 round 2).
    const standing = usePhoneCode || sentFactor?.strategy === 'email_code';
    if (!standing || !sentFactor || sendingFor.current === attempt.current || resendIn > 0 || inFlight.current) return;
    setCode('');
    setError('');
    void send(sentFactor, attempt.current);
  };

  const verify = async () => {
    if (inFlight.current) return;
    const trimmed = code.trim();
    if (!trimmed) {
      setError(useBackupCode ? 'Enter a backup code' : 'Enter the 6-digit code');
      return;
    }

    // Before the spinner starts, and out loud. The panel only opens after an
    // isLoaded-guarded Clerk call, so this is not reachable from a screen today, but
    // it used to return silently from inside the try: a Verify that did nothing
    // (web-app#384 review).
    if (!isLoaded) {
      setError(SIGN_IN_STILL_LOADING);
      return;
    }

    inFlight.current = true;
    const thisAttempt = attempt.current;
    setIsVerifying(true);
    setError('');
    let signedIn = false;
    // ONE verifying window around two Clerk calls, each with its own catch. They
    // fail for different reasons, and the controls stay disabled until both settle.
    try {
      let sessionId: string;
      try {
        // Which way this attempt verifies. The state block above defines the
        // two flags; the standing-on-sent derivation is their single source of
        // truth: an explicit backup-code toggle wins (its whole job, on either
        // base), then the code Clerk sends — but only when the attempt is
        // actually STANDING on it. A phone factor offered beside a held
        // authenticator has sentFactor set while usePhoneCode is false, and
        // that attempt must verify as totp — reading sentFactor alone here
        // submitted a TOTP code as a phone code (caught in review).
        // The Device Trust email needs no flag: when it is the sent factor, it
        // is always the base being completed.
        const standingOnSent = usePhoneCode || sentFactor?.strategy === 'email_code';
        const result = await signIn.attemptSecondFactor(
          useBackupCode
            ? { strategy: 'backup_code', code: trimmed }
            : standingOnSent && sentFactor
              ? { strategy: sentFactor.strategy, code: trimmed }
              : { strategy: 'totp', code: trimmed },
        );
        // Abandoned while Clerk was answering: say nothing, activate nothing.
        if (attempt.current !== thisAttempt) return;
        if (result.status !== 'complete') {
          // Name the status rather than inventing a reason for it.
          setError(describeIncompleteStatus(result.status));
          return;
        }
        // Typed nullable. setActive({ session: null }) is not a sign-in (web-app#384 review).
        if (!result.createdSessionId) {
          setError(SIGN_IN_NOT_FINISHED);
          return;
        }
        sessionId = result.createdSessionId;
      } catch (err) {
        // The same rule for a late REJECTION: its error would land on a panel the
        // user may already have entered again (Fury's gate on 7c7b072).
        if (attempt.current !== thisAttempt) return;
        setError(
          clerkErrorMessage(
            err,
            'That code was not accepted.',
            "Couldn't verify that code. Please try again.",
          ),
        );
        return;
      }

      // The code IS accepted from here. A session that fails to activate is not a
      // rejected code, and read as one while this shared the catch above
      // (web-app#384 review). Clerk's own words when it sends them.
      try {
        await setActive({ session: sessionId });
        // NO attempt re-check here, deliberately (JAR-1560). The session is active
        // now, and skipping onComplete would not un-sign anyone in; it would only
        // leave the flow unfinished. It would also misfire: App's effect navigates
        // off /auth/ as soon as Clerk reports the session (settle() and isEntryPath),
        // which unmounts the screen and bumps the attempt, so a re-check would drop
        // handleSignIn (return-to, login tracking, a plan's checkout) depending on
        // render timing. Abandonment is enforced where it matters: before setActive.
        signedIn = true;
      } catch (err) {
        if (attempt.current !== thisAttempt) return;
        setError(clerkErrorMessage(err, SIGN_IN_NOT_FINISHED));
      }
    } finally {
      inFlight.current = false;
      setIsVerifying(false);
    }
    // OUTSIDE the try. What the screen does next can fail on its own, and inside the
    // try that failure read "That code was not accepted" with the session already
    // active (web-app#384 review). verify() rejects instead. The panel calls it with
    // `void`, so the rejection reaches App's global unhandledrejection net, which
    // reports it through captureException and shows "Something went wrong".
    if (signedIn) onComplete(email, remember);
  };

  const toggleBackupCode = () => {
    setUseBackupCode((v) => !v);
    setCode('');
    setError('');
  };

  /**
   * Hop between the texted code and the authenticator (JAR-2872). A hop onto
   * the text is the only place a phone send starts outside enter(), and the
   * same guards resend() applies hold here too — a send already in flight for
   * this attempt, or a cooldown still counting, is not a second text (AI
   * first-pass, web-app#859: toggling off/on had re-sent every time). Hopping
   * off to the authenticator sends nothing, and the text simply expires.
   */
  const togglePhoneCode = () => {
    if (usePhoneCode) {
      // Back to the authenticator. The text, if one went out, simply expires.
      setUsePhoneCode(false);
      setCode('');
      setError('');
      return;
    }
    if (useBackupCode) setUseBackupCode(false);
    setUsePhoneCode(true);
    setCode('');
    setError('');
    // The PHONE factor is what gets sent here — never whatever else the attempt
    // carries (an email factor must not be re-sent by a phone hop; web-app#859
    // round 2). Guards as above: in flight or cooling down is not a second text.
    if (sentFactor?.strategy === 'phone_code' && sendingFor.current !== attempt.current && resendIn === 0) {
      void send(sentFactor, attempt.current);
    }
  };

  /** Leave the second factor. The Clerk attempt is abandoned, not cancelled: the
   *  next submit starts a fresh one. */
  const reset = () => {
    // isVerifying is NOT cleared here (web-app#384 review suggested it): the request
    // is still in flight, and clearing it early would re-enable the controls while
    // it runs. It clears when the request settles; the bump below is what makes the
    // late answer harmless.
    attempt.current += 1;
    setIsActive(false);
    setCode('');
    setError('');
    setUseBackupCode(false);
    setUsePhoneCode(false);
    setSentFactor(null);
    setSendState('idle');
    setResendIn(0);
  };

  return {
    active,
    enter,
    reset,
    code,
    setCode,
    useBackupCode,
    toggleBackupCode,
    error,
    isVerifying,
    options,
    verify,
    /** True when this attempt is being completed by a code Clerk sent — the
     *  texted code of a phone attempt, or the Device Trust email. Gates the
     *  prompt, heading and Resend machinery, so "carries but is not standing on"
     *  (a phone offered beside a held authenticator) shows none of them. */
    viaSentCode: usePhoneCode || sentFactor?.strategy === 'email_code',
    /** Clerk's masked address or phone number the code went to, or '' when there is none. */
    sentHint: sentFactor?.safeIdentifier ?? '',
    /** True when standing on the texted code (an enrolled phone factor). */
    viaPhone: usePhoneCode,
    /** True when the attempt carries a phone factor at all (standing on it, or
     *  offering it beside a held authenticator). Gates the texted-code switch. */
    phoneOffered:
      usePhoneCode ||
      (options.totp || options.backup ? sentFactor?.strategy === 'phone_code' : false),
    togglePhoneCode,
    sendState,
    isSending: sendState === 'sending',
    /** Seconds until Resend is available again; 0 when it is available now. */
    resendIn,
    resend,
  };
}

export type SecondFactor = ReturnType<typeof useSecondFactor>;
